import { useState } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader, Card, Btn, Input, InfoAlert, Badge, DetailRow } from '../components/UI';
import { fmt } from '../utils/helpers';

/**
 * "The customer says they paid but their wallet is empty."
 *
 * Queries Flutterwave directly by reference so you never have to open their
 * dashboard, shows whether we already booked it locally, and credits it if
 * not. Crediting is idempotent — the stored reference is Flutterwave's numeric
 * id and it is uniquely indexed, so a double click cannot double-credit.
 */
export default function Payments() {
  const [reference, setReference] = useState('');
  const [result, setResult]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [crediting, setCrediting] = useState(false);
  const toast = useToast();

  async function lookup(e) {
    e?.preventDefault();
    if (!reference.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.lookupPayment(reference.trim());
      setResult(r);
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  async function credit() {
    setCrediting(true);
    try {
      const r = await api.creditPayment(reference.trim(), result?.matched_user?.id);
      toast(r.message || 'Credited.', 'success');
      lookup();
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setCrediting(false);
    }
  }

  const flw = result?.flutterwave;

  return (
    <div>
      <PageHeader
        title="Payment Lookup"
        subtitle="Find a Flutterwave payment by reference and credit it if it was missed."
      />

      <InfoAlert>
        Accepts either Flutterwave's numeric transaction ID or the <code>tx_ref</code> the
        customer sees. A missed webhook is the usual reason a payment never reached a wallet.
      </InfoAlert>

      <Card style={{ marginTop: 16 }}>
        <form onSubmit={lookup} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Input
              label="Transaction reference"
              value={reference}
              onChange={setReference}
              placeholder="e.g. 2074655927 or wallet-4ba85fe1-…"
              required
            />
          </div>
          <Btn type="submit" loading={loading}>Look up</Btn>
        </form>
      </Card>

      {result && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 15, color: 'var(--text)' }}>On Flutterwave</h3>
            <Badge color={flw?.status === 'successful' ? 'green' : flw?.status === 'pending' ? 'yellow' : 'red'}>
              {flw?.status || 'unknown'}
            </Badge>
            {result.already_credited
              ? <Badge color="green">already credited</Badge>
              : <Badge color="yellow">not credited</Badge>}
          </div>

          <DetailRow label="Amount"        value={`${fmt.naira(flw?.amount)} ${flw?.currency || ''}`} />
          <DetailRow label="Transaction ID" value={flw?.id} />
          <DetailRow label="tx_ref"        value={flw?.tx_ref || '—'} />
          <DetailRow label="Method"        value={flw?.payment_type || '—'} />
          <DetailRow label="Paid at"       value={flw?.created_at ? fmt.datetime(flw.created_at) : '—'} />
          <DetailRow label="Customer"      value={flw?.customer?.email || '—'} />
          <DetailRow
            label="Matched account"
            value={result.matched_user
              ? `${result.matched_user.name} (${result.matched_user.email})`
              : 'No account matches this email'}
          />

          {result.creditable && (
            <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <InfoAlert type="warning">
                This payment succeeded on Flutterwave but was never credited.
                Crediting adds {fmt.naira(flw?.amount)} to {result.matched_user?.email}.
              </InfoAlert>
              <Btn onClick={credit} loading={crediting} variant="success" style={{ marginTop: 12 }}>
                Credit {fmt.naira(flw?.amount)} to wallet
              </Btn>
            </div>
          )}

          {result.already_credited && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)', color: 'var(--text2)', fontSize: 13 }}>
              Already booked as transaction{' '}
              <code style={{ color: 'var(--text)' }}>{result.local_transaction?.id}</code>
              {result.local_transaction?.createdAt && ` on ${fmt.datetime(result.local_transaction.createdAt)}`}.
            </div>
          )}

          {!result.creditable && !result.already_credited && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)', color: 'var(--text2)', fontSize: 13 }}>
              {flw?.status !== 'successful'
                ? 'Not creditable — the payment did not succeed on Flutterwave.'
                : 'Not creditable — no account matches this payment. Ask the customer which email they registered with.'}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
